import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, ArrowLeft, Send, User, Clock, ShieldBan, Flag, Phone, PhoneOff, Lock, Search, Eye, MapPin, Inbox, ChevronRight, X } from 'lucide-react';
import { useAuth, API } from '../App';
import { categoryLabel, t } from '../i18n';
import Header from '../components/Header';
import { LocationChip } from '../components/LocationMapModal';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const Messages = () => {
  const { pingmeId } = useParams();
  const [searchParams] = useSearchParams();
  const finderConvId = searchParams.get('finder');
  const { user, features } = useAuth();

  // Tab: "owner" (my PingMe conversations) or "finder" (conversations I started as finder)
  const [activeTab, setActiveTab] = useState(finderConvId ? 'finder' : 'owner');

  // Owner mode state
  const [pingmes, setPingmes] = useState([]);
  const [summary, setSummary] = useState([]); // PingMes with active conversations (focused list)
  const [showAllPingmes, setShowAllPingmes] = useState(false);
  const [pingmeSearch, setPingmeSearch] = useState('');
  const [selectedPingme, setSelectedPingme] = useState(null);
  const [conversations, setConversations] = useState([]);

  // Finder mode state
  const [finderConversations, setFinderConversations] = useState([]);

  // Shared state
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchPingmes();
    fetchSummary();
    fetchFinderConversations();
  }, []);

  useEffect(() => {
    if (pingmeId) {
      const pingme = pingmes.find(p => p.pingme_id === pingmeId);
      if (pingme) {
        setActiveTab('owner');
        setSelectedPingme(pingme);
        fetchConversations(pingmeId);
      }
    } else if (summary.length > 0 && !selectedPingme && activeTab === 'owner') {
      const first = pingmes.find(p => p.pingme_id === summary[0].pingme_id) || summary[0];
      setSelectedPingme(first);
      fetchConversations(first.pingme_id);
    }
  }, [pingmeId, pingmes, summary]);

  // Auto-open finder conversation from URL param
  useEffect(() => {
    if (finderConvId && finderConversations.length > 0) {
      const conv = finderConversations.find(c => c.conversation_id === finderConvId);
      if (conv) selectFinderConversation(conv);
    }
  }, [finderConvId, finderConversations]);

  const fetchPingmes = async () => {
    try {
      const response = await axios.get(`${API}/pingmes`, { withCredentials: true });
      setPingmes(response.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/conversations/owner-summary`, { withCredentials: true });
      setSummary(response.data || []);
    } catch { /* silent */ }
  };

  const fetchFinderConversations = async () => {
    try {
      const response = await axios.get(`${API}/conversations/my-finder`, { withCredentials: true });
      setFinderConversations(response.data);
    } catch { /* silent */ }
  };

  const fetchConversations = async (id) => {
    try {
      const response = await axios.get(`${API}/conversations/pingme/${id}`, { withCredentials: true });
      setConversations(response.data);
      if (response.data.length > 0 && !selectedConversation) {
        selectConversation(response.data[0]);
      }
    } catch {
      fetchMessages(id);
    }
  };

  const fetchMessages = async (id) => {
    try {
      const response = await axios.get(`${API}/messages/${id}`, { withCredentials: true });
      setMessages(response.data);
    } catch { /* silent */ }
  };

  const selectPingme = (pingme) => {
    setSelectedPingme(pingme);
    setSelectedConversation(null);
    setMessages([]);
    fetchConversations(pingme.pingme_id);
  };

  const selectConversation = async (conv) => {
    setSelectedConversation({ ...conv, _mode: 'owner' });
    try {
      const response = await axios.get(`${API}/conversations/${conv.conversation_id}`, { withCredentials: true });
      setMessages(response.data.messages || []);
      setSelectedConversation({ ...conv, is_blocked: response.data.is_blocked, is_reported: response.data.is_reported, phone_shared: response.data.phone_shared, _mode: 'owner' });
      // Refresh summary so unread counters update
      fetchSummary();
    } catch { /* silent */ }
  };

  const selectFinderConversation = async (conv) => {
    setSelectedConversation({ ...conv, _mode: 'finder' });
    try {
      const response = await axios.get(`${API}/conversations/${conv.conversation_id}`, { withCredentials: true });
      setMessages(response.data.messages || []);
      setSelectedConversation({ ...conv, is_blocked: response.data.is_blocked, phone_shared: response.data.phone_shared, shared_phone: response.data.shared_phone, _mode: 'finder' });
    } catch { /* silent */ }
  };

  const blockSender = async (convId) => {
    try {
      const conv = selectedConversation;
      if (conv?.is_blocked) {
        await axios.post(`${API}/conversations/${convId}/unblock`, {}, { withCredentials: true });
        toast.success('Expediteur debloque');
        setSelectedConversation({ ...conv, is_blocked: false });
      } else {
        await axios.post(`${API}/conversations/${convId}/block`, {}, { withCredentials: true });
        toast.success('Expediteur bloque');
        setSelectedConversation({ ...conv, is_blocked: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const reportSender = async (convId) => {
    if (!window.confirm('Signaler cet expediteur ? Il sera bloque automatiquement.')) return;
    try {
      await axios.post(`${API}/conversations/${convId}/report`, { reason: 'inappropriate' }, { withCredentials: true });
      toast.success('Signalement envoye');
      setSelectedConversation({ ...selectedConversation, is_blocked: true, is_reported: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const sharePhone = async (convId, share) => {
    try {
      await axios.post(`${API}/conversations/${convId}/share-phone`, { share }, { withCredentials: true });
      toast.success(share ? 'Numero partage' : 'Partage retire');
      setSelectedConversation({ ...selectedConversation, phone_shared: share });
      const response = await axios.get(`${API}/conversations/${convId}`, { withCredentials: true });
      setMessages(response.data.messages || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const isOwnerMode = selectedConversation?._mode === 'owner';
      const pingme_id = isOwnerMode ? selectedPingme.pingme_id : selectedConversation.pingme_id;
      
      const payload = {
        pingme_id,
        content: newMessage,
        sender_name: user.name,
        conversation_id: selectedConversation.conversation_id
      };

      const response = await axios.post(`${API}/messages`, payload, { withCredentials: true });
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds <= 0) return 'Expire';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  if (features?.messaging_enabled === false) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-charcoal/50">{t('messages.messagingDisabled')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  const isOwnerMode = selectedConversation?._mode === 'owner';
  const isFinderMode = selectedConversation?._mode === 'finder';

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="px-6 sm:px-8 lg:px-12 py-8">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/dashboard"
            data-testid="back-to-dashboard"
            className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('messages.backToDashboard')}
          </Link>

          {/* Tabs: Owner / Finder */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedConversation(null); setMessages([]); }} className="mb-6">
            <TabsList className="bg-white border-2 border-charcoal rounded-full p-1" data-testid="messages-tabs">
              <TabsTrigger value="owner" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal px-6" data-testid="tab-owner">
                <MessageCircle className="w-4 h-4 mr-2" /> {t('messages.myPingmes')}
              </TabsTrigger>
              <TabsTrigger value="finder" className="rounded-full data-[state=active]:bg-accent data-[state=active]:text-charcoal px-6" data-testid="tab-finder">
                <Search className="w-4 h-4 mr-2" /> {t('messages.foundObjects')}
                {finderConversations.length > 0 && (
                  <span className="ml-2 bg-accent text-charcoal text-xs font-bold px-1.5 py-0.5 rounded-full border border-charcoal">{finderConversations.length}</span>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="grid lg:grid-cols-4 gap-6">
            {/* Sidebar - hidden on mobile when a conversation is open */}
            <div className={`lg:col-span-1 ${selectedConversation ? 'hidden lg:block' : 'block'}`}>
              {activeTab === 'owner' ? (
                <>
                  <div className="card-brutal p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="font-bold text-charcoal font-heading flex items-center gap-2">
                        <Inbox className="w-4 h-4" />
                        {showAllPingmes ? t('messages.myPingmes') : 'Conversations actives'}
                      </h2>
                      {summary.reduce((acc, s) => acc + (s.unread_count || 0), 0) > 0 && (
                        <span className="bg-danger text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                          {summary.reduce((acc, s) => acc + (s.unread_count || 0), 0)} non lus
                        </span>
                      )}
                    </div>

                    {/* Search */}
                    <div className="relative mb-3">
                      <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
                      <input
                        type="text"
                        value={pingmeSearch}
                        onChange={(e) => setPingmeSearch(e.target.value)}
                        data-testid="pingme-search-input"
                        placeholder="Rechercher un PingMe..."
                        className="w-full pl-8 pr-8 py-2 text-sm rounded-lg border-2 border-charcoal/15 focus:border-charcoal outline-none"
                      />
                      {pingmeSearch && (
                        <button
                          onClick={() => setPingmeSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-charcoal/40 hover:text-charcoal"
                          data-testid="pingme-search-clear"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {pingmes.length === 0 ? (
                      <p className="text-charcoal/70 text-sm">{t('dashboard.noPingmes')}</p>
                    ) : (() => {
                      const q = pingmeSearch.trim().toLowerCase();
                      const summaryById = new Map(summary.map((s) => [s.pingme_id, s]));
                      const baseList = showAllPingmes
                        ? pingmes.map((p) => ({ ...p, ...(summaryById.get(p.pingme_id) || { unread_count: 0, conv_count: 0, last_activity: null }) }))
                        : summary.map((s) => ({ ...s, ...(pingmes.find((p) => p.pingme_id === s.pingme_id) || {}), ...s }));
                      // Sort: unread first, then last_activity
                      const sorted = [...baseList].sort((a, b) => {
                        if ((a.unread_count || 0) !== (b.unread_count || 0)) return (b.unread_count || 0) - (a.unread_count || 0);
                        const at = a.last_activity ? Date.parse(a.last_activity) : 0;
                        const bt = b.last_activity ? Date.parse(b.last_activity) : 0;
                        return bt - at;
                      });
                      const filtered = q
                        ? sorted.filter((p) => (p.name || '').toLowerCase().includes(q) || (categoryLabel(p.category) || '').toLowerCase().includes(q))
                        : sorted;

                      if (!showAllPingmes && filtered.length === 0) {
                        return (
                          <div className="text-center py-6">
                            <Inbox className="w-10 h-10 text-charcoal/15 mx-auto mb-2" />
                            <p className="text-charcoal/50 text-sm font-bold">Aucune conversation</p>
                            <p className="text-charcoal/40 text-xs mt-1">Personne n'a scanne vos PingMe pour l'instant.</p>
                          </div>
                        );
                      }
                      if (showAllPingmes && filtered.length === 0) {
                        return <p className="text-charcoal/40 text-sm text-center py-4">Aucun PingMe ne correspond.</p>;
                      }

                      return (
                        <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                          {filtered.map((pingme) => {
                            const isSelected = selectedPingme?.pingme_id === pingme.pingme_id;
                            const unread = pingme.unread_count || 0;
                            const convCount = pingme.conv_count || 0;
                            return (
                              <button
                                key={pingme.pingme_id}
                                onClick={() => selectPingme(pingme)}
                                data-testid={`select-pingme-${pingme.pingme_id}`}
                                className={`w-full text-left p-2.5 rounded-xl border-2 transition-all flex items-center gap-2.5 ${
                                  isSelected
                                    ? 'border-charcoal bg-primary/10'
                                    : unread > 0
                                      ? 'border-danger/40 bg-danger/5 hover:border-danger'
                                      : 'border-charcoal/15 hover:border-charcoal/40'
                                }`}
                              >
                                {/* Avatar */}
                                <div
                                  className="w-9 h-9 rounded-lg border border-charcoal/30 shrink-0 flex items-center justify-center text-xs font-bold uppercase"
                                  style={{ backgroundColor: pingme.bg_color || '#FFE082' }}
                                >
                                  {pingme.photo_url ? (
                                    <img src={pingme.photo_url.startsWith('/api') ? `${API.replace('/api', '')}${pingme.photo_url}` : pingme.photo_url} alt="" className="w-full h-full rounded-lg object-cover" />
                                  ) : (
                                    <span>{(pingme.name || '?').charAt(0)}</span>
                                  )}
                                </div>

                                {/* Body */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-1">
                                    <p className={`text-sm truncate ${unread > 0 ? 'font-bold text-charcoal' : 'font-semibold text-charcoal/80'}`}>{pingme.name}</p>
                                    {unread > 0 && (
                                      <span className="bg-danger text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center shrink-0">
                                        {unread}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-charcoal/50 truncate">
                                    {convCount > 0 ? `${convCount} conv.` : (showAllPingmes ? 'Aucune conv.' : '')}
                                    {pingme.is_lost ? ' · PERDU' : ''}
                                  </p>
                                </div>
                                <ChevronRight className="w-3.5 h-3.5 text-charcoal/30 shrink-0" />
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Toggle all/active */}
                    <button
                      onClick={() => setShowAllPingmes((v) => !v)}
                      data-testid="toggle-show-all-pingmes"
                      className="w-full mt-3 text-[11px] font-bold tracking-[0.08em] uppercase text-charcoal/60 hover:text-charcoal text-center py-1.5 border-t border-charcoal/10"
                    >
                      {showAllPingmes
                        ? `← Voir uniquement les conversations (${summary.length})`
                        : `Voir toutes mes PingMe (${pingmes.length}) →`}
                    </button>
                  </div>

                  {selectedPingme && conversations.length > 0 && (
                    <div className="card-brutal p-4">
                      <h3 className="font-bold text-charcoal font-heading mb-3 text-sm flex items-center gap-2">
                        <MessageCircle className="w-3.5 h-3.5" />
                        {selectedPingme.name}
                      </h3>
                      <div className="space-y-1.5">
                        {conversations.map((conv) => (
                          <button
                            key={conv.conversation_id}
                            onClick={() => selectConversation(conv)}
                            data-testid={`select-conv-${conv.conversation_id}`}
                            className={`w-full text-left p-2.5 rounded-xl border-2 transition-all ${
                              selectedConversation?.conversation_id === conv.conversation_id
                                ? 'border-charcoal bg-accent/20'
                                : conv.unread_count > 0
                                  ? 'border-danger/40 bg-danger/5 hover:border-danger'
                                  : 'border-charcoal/15 hover:border-charcoal/40'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <p className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold' : 'font-semibold'} text-charcoal`}>{conv.sender_name}</p>
                              <div className="flex items-center gap-1 shrink-0">
                                {conv.is_blocked && <ShieldBan className="w-3 h-3 text-danger" />}
                                {conv.unread_count > 0 && (
                                  <span className="bg-danger text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                    {conv.unread_count}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 text-[10px] text-charcoal/50">
                              <Clock className="w-2.5 h-2.5" />
                              <span>{formatTimeRemaining(conv.remaining_seconds)}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="card-brutal p-4">
                  <h2 className="font-bold text-charcoal font-heading mb-4">{t('messages.foundObjects')}</h2>
                  <p className="text-xs text-charcoal/50 mb-4">Conversations que vous avez initiees en scannant un QR code</p>
                  {finderConversations.length === 0 ? (
                    <div className="text-center py-4">
                      <Search className="w-8 h-8 text-charcoal/20 mx-auto mb-2" />
                      <p className="text-charcoal/50 text-sm">{t('messages.noConversation')}</p>
                      <p className="text-xs text-charcoal/40 mt-1">{t('messages.scanToStart')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {finderConversations.map((conv) => (
                        <button
                          key={conv.conversation_id}
                          onClick={() => selectFinderConversation(conv)}
                          data-testid={`select-finder-conv-${conv.conversation_id}`}
                          className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                            selectedConversation?.conversation_id === conv.conversation_id
                              ? 'border-accent bg-accent/10'
                              : 'border-charcoal/20 hover:border-charcoal'
                          }`}
                        >
                          <p className="font-bold text-charcoal text-sm truncate">{conv.pingme_name}</p>
                          <p className="text-xs text-charcoal/50">{categoryLabel(conv.pingme_category)}</p>
                          <div className="flex justify-between items-center mt-1">
                            <div className="flex items-center gap-1 text-xs text-charcoal/60">
                              <Clock className="w-3 h-3" />
                              <span>{formatTimeRemaining(conv.remaining_seconds)}</span>
                            </div>
                            {conv.unread_count > 0 && (
                              <span className="bg-danger text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Messages Panel - hidden on mobile when no conversation selected */}
            <div className={`lg:col-span-3 ${selectedConversation ? 'block' : 'hidden lg:block'}`}>
              <div className="card-brutal flex flex-col h-[600px]">
                {!selectedConversation ? (
                  <div className="flex-1 flex items-center justify-center text-center p-8">
                    <div>
                      <MessageCircle className="w-16 h-16 text-charcoal/20 mx-auto mb-4" />
                      <p className="text-charcoal/70">
                        {activeTab === 'owner'
                          ? t('messages.selectPingme')
                          : t('messages.selectConversation')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Conversation Header */}
                    <div className="border-b-2 border-charcoal p-4">
                      <div className="flex justify-between items-center gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Mobile back button */}
                          <button
                            onClick={() => { setSelectedConversation(null); setMessages([]); }}
                            data-testid="mobile-back-to-list"
                            className="lg:hidden p-1.5 rounded-full hover:bg-surface-alt shrink-0"
                            aria-label="Retour"
                          >
                            <ArrowLeft className="w-4 h-4" />
                          </button>
                          <div className="min-w-0">
                          {isOwnerMode ? (
                            <>
                              <h2 className="font-bold text-charcoal font-heading truncate">{selectedPingme?.name}</h2>
                              <p className="text-sm text-charcoal/60 truncate">
                                {t('messages.conversationWith')} {selectedConversation.sender_name}
                              </p>
                            </>
                          ) : (
                            <>
                              <h2 className="font-bold text-charcoal font-heading truncate">{selectedConversation.pingme_name}</h2>
                              <p className="text-sm text-charcoal/60 truncate">
                                {t('messages.asFinderLabel')}
                              </p>
                            </>
                          )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isOwnerMode && (
                            <>
                              <button
                                onClick={() => blockSender(selectedConversation.conversation_id)}
                                data-testid="block-sender-btn"
                                className={`p-2 rounded-full border-2 transition-all ${
                                  selectedConversation.is_blocked
                                    ? 'bg-danger/10 border-danger text-danger'
                                    : 'border-charcoal/20 text-charcoal/60 hover:border-charcoal'
                                }`}
                                title={selectedConversation.is_blocked ? 'Debloquer' : 'Bloquer'}
                              >
                                <ShieldBan className="w-4 h-4" />
                              </button>
                              {!selectedConversation.is_reported && (
                                <button
                                  onClick={() => reportSender(selectedConversation.conversation_id)}
                                  data-testid="report-sender-btn"
                                  className="p-2 rounded-full border-2 border-charcoal/20 text-charcoal/60 hover:border-danger hover:text-danger transition-all"
                                  title="Signaler"
                                >
                                  <Flag className="w-4 h-4" />
                                </button>
                              )}
                              {selectedConversation.is_reported && (
                                <span className="text-xs text-danger font-bold px-2 py-1 bg-danger/10 rounded-full border border-danger/30">{t('messages.reported')}</span>
                              )}
                              <button
                                onClick={() => sharePhone(selectedConversation.conversation_id, !selectedConversation.phone_shared)}
                                data-testid="share-phone-btn"
                                className={`p-2 rounded-full border-2 transition-all ${
                                  selectedConversation.phone_shared
                                    ? 'bg-green-100 border-green-500 text-green-700'
                                    : 'border-charcoal/20 text-charcoal/60 hover:border-charcoal'
                                }`}
                                title={selectedConversation.phone_shared ? 'Retirer le partage du numero' : 'Partager mon numero'}
                              >
                                {selectedConversation.phone_shared ? <Phone className="w-4 h-4" /> : <PhoneOff className="w-4 h-4" />}
                              </button>
                            </>
                          )}
                          {isFinderMode && selectedConversation.shared_phone && (
                            <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full border border-green-300 text-sm font-bold">
                              <Phone className="w-4 h-4" />
                              {selectedConversation.shared_phone}
                            </div>
                          )}
                          <div className="flex items-center gap-2 bg-accent/30 rounded-full px-3 py-1">
                            <Clock className="w-4 h-4 text-charcoal" />
                            <span className="text-sm font-bold text-charcoal">
                              {formatTimeRemaining(selectedConversation.remaining_seconds)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Messages List */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 ? (
                        <div className="text-center py-12">
                          <MessageCircle className="w-12 h-12 text-charcoal/20 mx-auto mb-3" />
                          <p className="text-charcoal/70">{t('messages.noMessages')}</p>
                        </div>
                      ) : (
                        messages.map((msg) => {
                          // In owner mode: owner messages on right, finder on left
                          // In finder mode: finder messages on right, owner on left
                          const isMine = isOwnerMode ? msg.is_from_owner : !msg.is_from_owner;
                          return (
                            <div
                              key={msg.message_id}
                              data-testid={`message-${msg.message_id}`}
                              className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[80%] ${
                                  isMine
                                    ? 'bg-primary text-charcoal border-2 border-charcoal rounded-2xl rounded-br-none'
                                    : 'bg-white text-charcoal border-2 border-charcoal rounded-2xl rounded-bl-none'
                                } p-4`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <User className="w-4 h-4" />
                                  <span className="text-sm font-bold">{msg.sender_name}</span>
                                </div>
                                <p className="text-sm">{msg.content}</p>
                                {msg.location && (
                                  <p className="text-xs opacity-70 mt-2 flex items-center gap-1">
                                    <MapPin className="w-3 h-3 shrink-0" />
                                    <LocationChip value={msg.location} />
                                  </p>
                                )}
                                <p className="text-xs opacity-50 mt-2">{formatDate(msg.created_at)}</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Reply Form */}
                    {selectedConversation?.is_blocked && isOwnerMode ? (
                      <div className="border-t-2 border-charcoal p-4 bg-danger/5">
                        <div className="flex items-center justify-center gap-2 text-danger text-sm font-bold">
                          <Lock className="w-4 h-4" />
                          {t('messages.blocked')}
                          <button onClick={() => blockSender(selectedConversation.conversation_id)} className="ml-2 text-xs underline hover:no-underline">Debloquer</button>
                        </div>
                      </div>
                    ) : selectedConversation?.is_blocked && isFinderMode ? (
                      <div className="border-t-2 border-charcoal p-4 bg-danger/5">
                        <div className="flex items-center justify-center gap-2 text-danger text-sm font-bold">
                          <Lock className="w-4 h-4" />
                          {t('messages.blockedByOwner')}
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={sendReply} className="border-t-2 border-charcoal p-4">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            data-testid="reply-input"
                            className="input-brutal flex-1"
                            placeholder={t('messages.replyPlaceholder')}
                          />
                          <button
                            type="submit"
                            disabled={sending || !newMessage.trim()}
                            data-testid="send-reply-btn"
                            className="btn-primary px-4 disabled:opacity-50"
                          >
                            <Send className="w-5 h-5" strokeWidth={2.5} />
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Messages;
